import { useDispatch, useSelector } from 'react-redux';
import { ILineResource } from '../../entities/farms.entities';
import { useHistory } from 'react-router-dom';
import {
  Button,
  CheckboxButton,
  Datepicker,
  Dropdown,
  Input,
} from '../../components/shared';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { useState } from 'react';
import { showFeedback } from '../../store/ui/ui.actions';
import moment from 'moment';
import { selectAssessmentFields } from '../../store/automation/automation.selector';
import { ICustomValue } from '../../entities/automation.entities';
import DateTimePicker from '../../components/shared/datepicker/DateTimePicker';
import { toSecTimeTracks } from '../../components/shared/input/TimeTrackInput';
import { sendSingleRequest } from '../../apis';
import { loadFarmsData } from '../../store/farms/farms.actions';
import FilesWall from '../../components/shared/pictures-wall/FilesWall';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { numToStr, toMillisecond } from '../../lib/common.helpers';
import { getEstHarvestDate } from '../../lib/farm.helpers';
import CustomValuesForm from '../../components/farm-util/CustomValuesForm';
import { IFileWall } from '../../entities/general.entities';

const getJSTime = (t: number | undefined) =>
  t ? toMillisecond(t) : moment().toDate().getTime();

interface IForm {
  size_min?: number;
  size_avg?: number;
  size_max?: number;
  assessment_date: number;
  planned_date_harvest: number;
  ready_to_harvest: boolean;
  comment?: string;
  start_time: number | null;
  finish_time: number | null;

  custom_values?: ICustomValue[];

  image_pdf_files?: IFileWall[];
}

interface Props {
  formID: number;
  data: any;
  lineData: ILineResource;
}

const PaperAssessForm = ({ formID, data, lineData }: Props) => {
  const dispatch = useDispatch<any>();
  const history = useHistory();
  const lang = useSelector(selectLang);
  const farmsData = useSelector(selectFarmsData);
  const customFields = useSelector(selectAssessmentFields);

  const [curFarm, setCurFarm] = useState(
    farmsData.find(f => f.id === lineData.farm_id),
  );
  const [curLine, setCurLine] = useState<ILineResource | undefined>(lineData);
  const [formData, setFormData] = useState<IForm>({
    size_min: data.size_min,
    size_avg: data.size_avg,
    size_max: data.size_max,
    assessment_date: getJSTime(data.assessment_date),
    planned_date_harvest: getJSTime(getEstHarvestDate(lineData)),
    ready_to_harvest: data.ready_to_harvest ?? false,
    start_time: data.start_time ? toMillisecond(data.start_time) : null,
    finish_time: data.finish_time ? toMillisecond(data.finish_time) : null,
    comment: data.comment,
    image_pdf_files: data.image_pdf_files,
    custom_values: data?.custom_values ?? undefined,
  });
  const [disabled, setDisabled] = useState(false);

  const showError = (message: string) =>
    dispatch(
      showFeedback({
        type: 'error',
        isMessage: true,
        message: translate(lang, message),
      }),
    );
  const selectFarm = (v: string) => {
    const farm = farmsData.find(x => x.id === Number(v));
    setCurFarm(farm);
    setCurLine(undefined);
  };
  const selectLine = (v: string) => {
    const line = curFarm?.lines.find(x => x.id === Number(v));
    setCurLine(line);
  };
  const updateForm = (key: keyof IForm, value: any) => {
    let tmp = { ...formData, [key]: value };
    if (key === 'size_min' || key === 'size_max') {
      tmp.size_avg = (Number(tmp.size_min) + Number(tmp.size_max)) / 2;
    }
    setFormData(tmp);
  };
  const confirmClick = async () => {
    if (!curFarm || !curLine) {
      showError('_sel_farm_line');
      return;
    }
    if (!formData.size_min || !formData.size_avg || !formData.size_max) {
      showError('_fill_size_fields');
      return;
    }
    if (Number(formData.size_min) > Number(formData.size_max)) {
      showError('_size_min_cannot_greater');
      return;
    }
    const {
      start_time,
      finish_time,
      assessment_date,
      planned_date_harvest,
      custom_values,
      ...params
    } = formData;
    const time_tracks = (start_time != null && finish_time != null)
      ? toSecTimeTracks([{ start_time, finish_time }])
      : undefined;
    if (start_time != null && finish_time != null && time_tracks === false) {
      showError('_invalid_time_range');
      return;
    }
    const submitData = {
      ...params,
      assessment_date: Math.floor(assessment_date / 1000),
      planned_date_harvest: Math.floor(planned_date_harvest / 1000),
      line_id: curLine.id,
      seeding_id: curLine.growing_cycle?.main_seed.id ?? data.seeding_id,
      custom_values: custom_values?.filter(x => !!x.field_val),
      time_tracks: time_tracks || undefined,
      id: formID,
    };
    if (curLine.id !== lineData.id && !curLine.growing_cycle) {
      showError('_line_empty');
      return;
    }
    if (!submitData.seeding_id) {
      showError('_line_empty');
      return;
    }
    setDisabled(true);
    const res = await sendSingleRequest(
      submitData,
      'POST',
      'api/queue/confirm-assessment',
      true,
    );
    if (res.status) {
      await dispatch(loadFarmsData());
      dispatch(
        showFeedback({
          isMessage: true,
          message: translate(lang, '_confirmed_ok'),
          type: 'success',
        }),
      );
      history.goBack();
    } else {
      setDisabled(false);
      showError(res.data?.message ?? 'Unknown error');
    }
  };

  const farmOptions = farmsData.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: `${x.name} - ${x.farm_number}`,
  }));
  const lineOptions = curFarm?.lines.map(x => ({
    id: x.id.toString(),
    value: x.id.toString(),
    label: x.line_name,
  }));

  return (
    <div>
      <div className='mb-17 d-flex'>
        <div className='w-100 mr-7'>
          <Dropdown
            label={translate(lang, 'Select farm')}
            options={farmOptions}
            value={curFarm?.id.toString()}
            onChange={v => selectFarm(v)}
          />
        </div>
        <div className='w-100 ml-7'>
          <Dropdown
            label={translate(lang, 'Select line')}
            options={lineOptions ?? []}
            value={curLine?.id.toString()}
            onChange={v => selectLine(v)}
          />
        </div>
      </div>
      <div className='d-flex mb-17 justify-between'>
        <div className='w-100'>
          <Input
            label={translate(lang, 'Size min')}
            type='number'
            value={numToStr(formData.size_min)}
            unit='mm'
            onChange={e => updateForm('size_min', e.target.value)}
          />
        </div>
        <div className='w-100 ml-12 mr-12'>
          <Input
            label={translate(lang, 'Size avg')}
            type='number'
            value={numToStr(formData.size_avg)}
            unit='mm'
            onChange={e => updateForm('size_avg', e.target.value)}
          />
        </div>
        <div className='w-100'>
          <Input
            label={translate(lang, 'Size max')}
            type='number'
            value={numToStr(formData.size_max)}
            unit='mm'
            onChange={e => updateForm('size_max', e.target.value)}
          />
        </div>
      </div>
      <div className='d-flex mb-17 mt-16 justify-between'>
        <div className='mr-12 w-100'>
          <Datepicker
            label={translate(lang, 'Assessment date')}
            defaultValue={formData.assessment_date}
            onChange={e =>
              updateForm(
                'assessment_date',
                e?.toDate().getTime() ?? moment().toDate().getTime(),
              )
            }
            required
          />
        </div>
        <div className='ml-12 w-100'>
          <Datepicker
            label={translate(lang, 'Planned date harvest')}
            defaultValue={formData.planned_date_harvest}
            onChange={e =>
              updateForm(
                'planned_date_harvest',
                e?.toDate().getTime() ?? moment().toDate().getTime(),
              )
            }
            required
          />
        </div>
      </div>
      <div className='d-flex mb-17 justify-between'>
        <DateTimePicker
          className='mr-12'
          defaultValue={formData.start_time}
          label={translate(lang, 'Start time')}
          onChange={e => updateForm('start_time', e?.toDate().getTime() ?? null)}
        />
        <DateTimePicker
          className='ml-12'
          defaultValue={formData.finish_time}
          label={translate(lang, 'Finish time')}
          onChange={e => updateForm('finish_time', e?.toDate().getTime() ?? null)}
        />
      </div>
      <div className='mb-17'>
        <CheckboxButton
          label={translate(lang, 'Ready to harvest')}
          checked={formData.ready_to_harvest}
          onChange={e => updateForm('ready_to_harvest', e.target.checked)}
        />
      </div>
      <CustomValuesForm
        fields={customFields}
        values={formData.custom_values ?? []}
        onChange={d => setFormData(prv => ({ ...prv, custom_values: d }))}
      />
      <div className='mb-17'>
        <Input
          label={translate(lang, 'Comment')}
          type='textarea'
          value={formData.comment ?? ''}
          placeholder={`${translate(lang, 'Type your comment')}...`}
          dataType='comment'
          onChange={e => updateForm('comment', e.target.value)}
        />
      </div>
      <div className='mb-17'>
        <FilesWall
          label={translate(lang, 'Images & PDF Files')}
          fileList={formData.image_pdf_files}
          onChange={f => updateForm('image_pdf_files', f)}
        />
      </div>
      <div className='mt-17 d-flex justify-end'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={confirmClick}
          disabled={disabled}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </div>
  );
};

export default PaperAssessForm;
